// src/pages/JourneyMap/phases/Step2Creation.jsx
// Passo 2 — CRIAÇÃO: gera criativo com IA, renderiza via CreativeCanvasEngine, persiste
import React, { useState, useEffect } from 'react';
import EdyAssistant from '../components/EdyAssistant';
import CreativeCanvasEngine from '../../../engine/CreativeCanvasEngine/CreativeCanvasEngine';
import apiClient from '../../../lib/apiClient';

export default function Step2Creation({ campaignId, profileData, onAdvance }) {
  const [structure, setStructure]   = useState(null);
  const [creativeId, setCreativeId] = useState(null);
  const [audience, setAudience]     = useState(null);
  const [headline, setHeadline]     = useState('');
  const [imageUrl, setImageUrl]     = useState('');
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [edyMood, setEdyMood]       = useState('think');
  const [edyMsg, setEdyMsg]         = useState('Vou criar uma estratégia campeã baseada no seu perfil...');

  // Gera criativo automaticamente ao montar
  useEffect(() => {
    generateCreative();
  }, []); // eslint-disable-line

  const generateCreative = async () => {
    setLoading(true);
    setError('');
    setEdyMsg('Estou gerando seu criativo com IA...');
    setEdyMood('think');

    try {
      const r = await apiClient.request('/creative/generate', {
        method: 'POST',
        body:   JSON.stringify({
          business_name:  profileData?.empresa     || 'Meu Negócio',
          product_name:   profileData?.diferencial || 'Produto Incrível',
          niche:          profileData?.dor_cliente  || 'geral',
          tone:           'urgente',
          platform:       'instagram',
          session_id:     '',
        }),
      });
      const d = await r.json();

      if (d.success && d.creative) {
        const c = d.creative;
        setCreativeId(c.id || c.creative_id);
        setHeadline(c.headline    || c.content?.headline || '');
        setImageUrl(c.image_url   || '');
        setAudience(c.audience    || c.publico_sugerido || null);

        // Monta estrutura para CreativeCanvasEngine
        const structureObj = c.structure || c.canvas_data || {
          canvas_data: {
            background_color: '#1a1a2e',
            layers: [
              { id: 'bg', type: 'image', src: c.image_url || '', x: 0, y: 0, width: 400, height: 400, zIndex: 0 },
              { id: 'hl', type: 'text',  text: c.headline || 'Seu Negócio em Destaque!', x: 20, y: 280, fontSize: 28, fontWeight: 'bold', color: '#ffffff', zIndex: 2 },
              { id: 'sub', type: 'text', text: c.legenda || c.caption || '', x: 20, y: 330, fontSize: 16, color: '#e2e8f0', zIndex: 2 },
            ],
          },
          _v: Date.now(),
        };
        structureObj._v = Date.now();
        setStructure(structureObj);

        setEdyMsg('Já montei uma estratégia campeã! Olha como ficou o seu criativo!');
        setEdyMood('joinha');
      } else {
        setError(d.message || 'Não foi possível gerar o criativo.');
        setEdyMsg('Hmm, tive um problema para gerar. Tente novamente!');
        setEdyMood('think');
      }
    } catch {
      setError('Erro de conexão ao gerar criativo.');
      setEdyMsg('Algo deu errado. Vamos tentar de novo!');
      setEdyMood('think');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvance = async () => {
    if (!creativeId) { setError('Criativo não gerado ainda.'); return; }
    setSaving(true);
    setEdyMsg('Salvando seu criativo no banco de dados...');
    try {
      const r = await apiClient.request(`/mp/journey/${campaignId}/creative`, {
        method: 'PUT',
        body:   JSON.stringify({
          creative_id:         creativeId,
          audience_suggestion: audience,
          headline,
          image_url:           imageUrl,
          legenda:             '',
        }),
      });
      const d = await r.json();
      if (d.success) {
        setEdyMsg('Criativo salvo com sucesso! Agora vamos refinar os detalhes');
        setEdyMood('joinha');
        setTimeout(() => onAdvance(d.campaign), 900);
      } else {
        setError(d.message || 'Erro ao salvar criativo.');
        setEdyMood('think');
      }
    } catch {
      setError('Erro de conexão ao salvar criativo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="jm-container">
      <EdyAssistant message={edyMsg} mood={edyMood} loading={loading} />

      <div className="jm-card">
        {loading ? (
          <div className="jm-loading">
            <div className="jm-spinner" />
            <span>Gerando criativo com IA...</span>
          </div>
        ) : error && !structure ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ color: 'var(--edy-red)', marginBottom: 16 }}>{error}</p>
            <button className="jm-btn-outline" onClick={generateCreative}>Tentar novamente</button>
          </div>
        ) : (
          <>
            {headline && (
              <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: 4 }}>{headline}</h3>
            )}

            {/* Criativo renderizado via CreativeCanvasEngine */}
            {structure && (
              <div className="jm-canvas-wrap">
                <CreativeCanvasEngine
                  key={structure._v}
                  structure={structure}
                  scale={0.28}
                />
              </div>
            )}

            {audience && (
              <div style={{
                background: 'rgba(34,211,238,.06)',
                border: '1px solid rgba(34,211,238,.2)',
                borderRadius: 10,
                padding: '12px 14px',
                fontSize: '0.82rem',
                color: 'var(--edy-muted)',
                marginBottom: 16,
              }}>
                <strong style={{ color: 'var(--edy-cyan)' }}>Público sugerido:</strong>{' '}
                {typeof audience === 'string' ? audience : JSON.stringify(audience)}
              </div>
            )}

            {error && <p style={{ color: 'var(--edy-red)', fontSize: '0.82rem', marginBottom: 12 }}>{error}</p>}

            <div className="jm-btn-row">
              <button className="jm-btn-ghost" onClick={generateCreative} disabled={loading || saving}>
                Regerar
              </button>
              <button className="jm-btn-cta" onClick={handleAdvance} disabled={!creativeId || saving}>
                {saving ? 'Salvando...' : 'Aprovar criativo →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
